/**
===================================================
Module: frontend.src.components.CompleteProfile.tsx
Description: First sign-in profile completion for invited users
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { apiService } from "../services/api";
import NotificationSystem, { Notification } from "./NotificationSystem";
import "../styles/CompleteProfile.css";

const CompleteProfile: React.FC = () => {
  const { t } = useTranslation("tenant");
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    last_name: "",
    first_name: "",
    company_name: "",
    address: "",
    phone_number: "",
    department: "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Seeded from the invitation: the company comes from their organisation and
  // the name from what the administrator entered, so most people only have to
  // add an address.
  useEffect(() => {
    if (!user) return;
    setForm((prev) => ({
      ...prev,
      last_name: user.last_name || prev.last_name,
      first_name: user.first_name || prev.first_name,
      company_name: user.company_name || prev.company_name,
      address: user.address || prev.address,
      phone_number: user.phone_number || prev.phone_number,
      department: user.department || prev.department,
    }));
  }, [user]);

  const removeNotification = (id: string) =>
    setNotifications((prev) => prev.filter((n) => n.id !== id));

  const set = (key: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: false }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const missing: Record<string, boolean> = {};
    if (!form.company_name.trim()) missing.company_name = true;
    if (!form.address.trim()) missing.address = true;
    if (Object.keys(missing).length) {
      setErrors(missing);
      return;
    }

    setIsSaving(true);
    try {
      await apiService.updateOwnProfile({
        company_name: form.company_name.trim(),
        address: form.address.trim(),
        phone_number: form.phone_number.trim(),
        department: form.department.trim(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
      });
      await refreshUser();
      navigate("/projects", { replace: true });
    } catch (error) {
      setNotifications((prev) => [
        ...prev,
        {
          id: Date.now().toString(),
          type: "error",
          message: error instanceof Error ? error.message : t("errors.generic"),
        },
      ]);
      setIsSaving(false);
    }
  };

  return (
    <div className="profile-setup">
      <div className="profile-card">
        <div className="profile-head">
          <h1>{t("profile.title")}</h1>
          <p>{t("profile.intro")}</p>
          {user?.email && <p className="profile-email">{user.email}</p>}
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="field-row">
            <div className="field">
              <label htmlFor="cp-last">{t("profile.lastName")}</label>
              <input
                id="cp-last"
                type="text"
                value={form.last_name}
                onChange={(e) => set("last_name", e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="cp-first">{t("profile.firstName")}</label>
              <input
                id="cp-first"
                type="text"
                value={form.first_name}
                onChange={(e) => set("first_name", e.target.value)}
              />
            </div>
          </div>

          <div className="field">
            <label htmlFor="cp-company">
              {t("profile.company")} <span className="req">*</span>
            </label>
            <input
              id="cp-company"
              type="text"
              value={form.company_name}
              className={errors.company_name ? "invalid" : ""}
              onChange={(e) => set("company_name", e.target.value)}
              required
            />
            {errors.company_name && (
              <span className="field-error">{t("common.required")}</span>
            )}
          </div>

          <div className="field">
            <label htmlFor="cp-address">
              {t("profile.address")} <span className="req">*</span>
            </label>
            <input
              id="cp-address"
              type="text"
              value={form.address}
              className={errors.address ? "invalid" : ""}
              onChange={(e) => set("address", e.target.value)}
              required
            />
            {errors.address && (
              <span className="field-error">{t("common.required")}</span>
            )}
          </div>

          <div className="field-row">
            <div className="field">
              <label htmlFor="cp-phone">{t("profile.phone")}</label>
              <input
                id="cp-phone"
                type="tel"
                value={form.phone_number}
                onChange={(e) => set("phone_number", e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="cp-dept">{t("profile.department")}</label>
              <input
                id="cp-dept"
                type="text"
                value={form.department}
                onChange={(e) => set("department", e.target.value)}
              />
            </div>
          </div>

          <div className="profile-actions">
            <button type="submit" className="btn-primary" disabled={isSaving}>
              {isSaving ? t("profile.saving") : t("profile.submit")}
            </button>
            <button type="button" className="link-btn" onClick={logout}>
              {t("profile.signOut")}
            </button>
          </div>
        </form>
      </div>

      <NotificationSystem
        notifications={notifications}
        onRemove={removeNotification}
      />
    </div>
  );
};

export default CompleteProfile;
